import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/** Local date-time suffix for download names: YYYYMMDD_HHMMSS */
export function exportFilenameStamp(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

export function escapeCsvCell(value) {
  const str = value == null ? "" : String(value);
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function exportToCsv(headers, rows, filename) {
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) => row.map(escapeCsvCell).join(",")),
  ];
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
}

export function exportToXlsx(headers, rows, filename, sheetName = "Datos") {
  exportWorkbook([{ name: sheetName, headers, rows }], filename);
}

export function exportWorkbook(sheets, filename) {
  const workbook = XLSX.utils.book_new();
  sheets.forEach(({ name, headers = [], rows = [] }) => {
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const widths = headers.map((header, index) => ({
      wch: Math.min(45, Math.max(
        String(header || "").length,
        ...rows.slice(0, 200).map((row) => String(row[index] ?? "").length),
        10
      ) + 2),
    }));
    worksheet["!cols"] = widths;
    XLSX.utils.book_append_sheet(workbook, worksheet, String(name).slice(0, 31));
  });
  XLSX.writeFile(workbook, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

async function captureElement(element, options = {}) {
  if (!element) throw new Error("No se encontró el contenido para exportar.");
  return html2canvas(element, {
    scale: options.scale || 2,
    useCORS: true,
    backgroundColor: options.backgroundColor || "#ffffff",
    logging: false,
    windowWidth: Math.max(element.scrollWidth, 1200),
  });
}

export async function exportElementToPng(element, filename, options = {}) {
  const canvas = await captureElement(element, options);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
  if (!blob) throw new Error("No se pudo generar la imagen PNG.");
  downloadBlob(blob, filename.endsWith(".png") ? filename : `${filename}.png`);
}

function sliceCanvas(source, startY, height) {
  const page = document.createElement("canvas");
  page.width = source.width;
  page.height = height;
  const ctx = page.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, page.width, page.height);
  ctx.drawImage(source, 0, startY, source.width, height, 0, 0, source.width, height);
  return page;
}

export async function exportElementToPdf(element, filename, options = {}) {
  const orientation = options.orientation || "portrait";
  const canvas = await captureElement(element, options);
  const pdf = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const margin = 8;
  const pageWidth = pdf.internal.pageSize.getWidth() - margin * 2;
  const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2;
  const pixelsPerPage = Math.floor((pageHeight / pageWidth) * canvas.width);
  const scaleY = canvas.height / Math.max(element.scrollHeight, 1);
  const boundaries = [...element.querySelectorAll("[data-export-chart]")]
    .map((node) => ({
      top: Math.round((node.offsetTop - element.offsetTop) * scaleY),
      bottom: Math.round((node.offsetTop - element.offsetTop + node.offsetHeight) * scaleY),
    }))
    .filter((item) => item.top >= 0 && item.bottom <= canvas.height);

  let offset = 0;
  let pageIndex = 0;
  while (offset < canvas.height) {
    let pageEnd = Math.min(offset + pixelsPerPage, canvas.height);
    const currentOffset = offset;
    const crossing = boundaries.find((item) =>
      item.top > currentOffset && item.top < pageEnd && item.bottom > pageEnd
    );
    if (crossing && crossing.top - offset > pixelsPerPage * 0.25) pageEnd = crossing.top;
    const height = Math.max(1, pageEnd - offset);
    const pageCanvas = sliceCanvas(canvas, offset, height);
    const imageHeight = (height * pageWidth) / canvas.width;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(pageCanvas.toDataURL("image/jpeg", 0.95), "JPEG", margin, margin, pageWidth, imageHeight);
    offset = pageEnd;
    pageIndex += 1;
  }

  pdf.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
}
